"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { BadgeScanner } from "./badge-scanner";

interface KioskConfig {
  identifierLabel: string;
  identifierPattern: string;
  identifierExample: string;
  personRecordLookup: boolean;
  autoResetSeconds: number;
}

export function KioskLanding({ kioskConfig }: { kioskConfig: KioskConfig }) {
  const router = useRouter();
  const [badge, setBadge] = useState("");
  const [error, setError] = useState("");
  const [validating, setValidating] = useState(false);
  const [scanning, setScanning] = useState(false);

  function handleScanned(value: string) {
    setBadge(value.toUpperCase());
    setScanning(false);
    setError("");
  }

  async function startNewRequest() {
    const value = await validate();
    if (!value) return;
    router.push(`/kiosk/submit?badge=${encodeURIComponent(value)}`);
  }

  async function viewMyRequests() {
    const value = await validate();
    if (!value) return;
    router.push(`/kiosk/my-requests?badge=${encodeURIComponent(value)}`);
  }

  /**
   * Returns the validated badge value (uppercased, trimmed) on success, or
   * an empty string if validation fails.
   *
   * - If `personRecordLookup` is true, the badge is verified against MAS via
   *   `/api/persons/<id>` (regex pattern is ignored — MAS is the source of truth).
   * - Otherwise, the regex pattern (if configured) gates the format only.
   */
  async function validate(): Promise<string> {
    const value = badge.trim().toUpperCase();
    if (!value) {
      setError(`${kioskConfig.identifierLabel} is required`);
      return "";
    }

    if (kioskConfig.personRecordLookup) {
      setValidating(true);
      setError("");
      try {
        const res = await fetch(`/api/persons/${encodeURIComponent(value)}`);
        if (res.status === 404) {
          setError(`${value} is not a recognized ${kioskConfig.identifierLabel}.`);
          return "";
        }
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data?.error || "Could not verify badge. Please try again.");
          return "";
        }
        return value;
      } catch {
        setError("Network error. Please try again.");
        return "";
      } finally {
        setValidating(false);
      }
    }

    // No backend lookup — fall back to the regex hint if configured.
    if (kioskConfig.identifierPattern) {
      const re = new RegExp(kioskConfig.identifierPattern);
      if (!re.test(value)) {
        setError(`Invalid format. Example: ${kioskConfig.identifierExample}`);
        return "";
      }
    }
    setError("");
    return value;
  }

  return (
    <div className="space-y-8">
      <div>
        <label className="block text-lg font-semibold mb-3">
          Enter or scan your {kioskConfig.identifierLabel}
        </label>
        <div className="flex gap-3">
          <input
            type="text"
            autoFocus
            value={badge}
            onChange={(e) => {
              setBadge(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") startNewRequest();
            }}
            placeholder={kioskConfig.identifierExample}
            disabled={validating}
            className="flex-1 text-2xl tracking-wider uppercase border-2 border-gray-300 rounded-[var(--radius)] px-5 py-4 focus:outline-none focus:border-primary disabled:opacity-60"
          />
          <button
            type="button"
            onClick={() => setScanning(true)}
            disabled={validating}
            aria-label="Scan badge"
            title="Scan badge"
            className="shrink-0 px-5 py-4 border-2 border-gray-300 rounded-[var(--radius)] hover:border-primary hover:bg-primary/5 disabled:opacity-60 transition-colors"
          >
            {/* simple camera glyph — keeps zero deps */}
            <span aria-hidden className="block text-2xl leading-none">
              &#x1F4F7;
            </span>
            <span className="sr-only">Scan badge</span>
          </button>
        </div>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        {validating && <p className="text-gray-500 text-sm mt-2">Verifying...</p>}
      </div>

      {scanning && (
        <BadgeScanner
          onScanned={handleScanned}
          onClose={() => setScanning(false)}
        />
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <button
          onClick={startNewRequest}
          disabled={validating}
          className="bg-primary text-white text-lg font-semibold py-6 rounded-[var(--radius)] hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          New Service Request
        </button>
        <button
          onClick={viewMyRequests}
          disabled={validating}
          className="border-2 border-primary text-primary text-lg font-semibold py-6 rounded-[var(--radius)] hover:bg-primary/5 transition-colors disabled:opacity-60"
        >
          View My Requests
        </button>
      </div>
    </div>
  );
}
